import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {FlowExecType} from '@mintdata-wsp/common/controllers/flow/FlowController';
import {InvokeFunctionExpr} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaASTNodes';
import {BaseFormulaVisitor} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaVisitor';
import {OhmParseResult} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmParseResult';
import {SheetAPIImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetAPIImpl';
import {FunctionUtil} from '@mintdata-wsp/common/controllers/spreadsheet/formula/invoke/FunctionUtil';
import {AllBuiltinFunctions} from '@mintdata-wsp/common/controllers/spreadsheet/functions/AllBuiltinFunctions';
import {ClientFunctionUtil} from '@mintdata-wsp/common/controllers/spreadsheet/functions/ClientFunctionUtil';
import {CloudFunction} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {
    AFunctionInvoker,
    ClientFunction,
    FunctionAlias,
    FunctionArg,
    FunctionArgType,
    FunctionId,
    FunctionName,
    FunctionNamespace,
    FunctionQualifiedName,
    FunctionReturn
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {FunctionSpec, IFunctionArgSpec} from '@mintdata/mint-kit/api/decorators/Function';
import {PropertyUtils} from '@mintdata/mint-kit/api/properties/PropertyUtils';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';
import {IReactionDisposer, reaction} from 'mobx';

export type ExecResult = IFrame | AError

export class ClientFunctionsController
{
    private _disposers:IReactionDisposer[] = [];

    private _qualifiedNameToFunc:Map<FunctionQualifiedName, ClientFunction> = new Map();
    private _aliasToFunc:Map<FunctionAlias, ClientFunction> = new Map();
    private _nameToFunc:Map<FunctionName, ClientFunction> = new Map();

    public init()
    {
        this._listenForCloudFunctionsChanges();
        this._buildFunctionIndexes();
    }

    private _clearFunctionIndexes()
    {
        this._qualifiedNameToFunc.clear();
        this._aliasToFunc.clear();
        this._nameToFunc.clear();
    }

    private _buildFunctionIndexes()
    {
        this._clearFunctionIndexes();
        this._buildLocalFunctionIndexes();
        this._buildCloudFunctionIndexes();
    }

    public dispose():void
    {
        this._disposers.forEach(disposer => disposer());
    }

    public searchForFunctions(searchText:string):ClientFunction[]
    {
        if( !searchText )
            return [];

        const matchingFunctions:ClientFunction[] = [];
        const functions = Array.from(this._nameToFunc.values());
        const ciSearchText = searchText.toUpperCase();

        for( let func of functions )
        {
            if( func.alias && func.alias.toUpperCase().indexOf(ciSearchText) !== -1 )
            {
                matchingFunctions.push(func);
            }
            else if( func.name && func.name.toUpperCase().indexOf(ciSearchText) !== -1 )
            {
                matchingFunctions.push(func);
            }
        }

        return matchingFunctions;
    }

    public findFunctionByName(namespace:FunctionNamespace, funcName:FunctionName):ClientFunction | null
    {
        const ciFuncName = funcName.toUpperCase();
        const ciQualifiedName = ClientFunctionUtil.toCIQualifiedName(namespace, funcName);

        return this._qualifiedNameToFunc.get(ciQualifiedName) || this._aliasToFunc.get(ciFuncName) || null;
    }

    private _listenForCloudFunctionsChanges()
    {
        const {flow} = commonControllers;

        const disposer = reaction(
            () => flow.getCloudFunctions(),
            this._onCloudFunctionsChanged
        );

        this._disposers.push(disposer);
    }

    private _onCloudFunctionsChanged = (cloudFunctions:CloudFunction[]) =>
    {
        this._buildFunctionIndexes();
    };

    private _buildLocalFunctionIndexes():void
    {
        for( const containerCtor of AllBuiltinFunctions.ContainerCtors )
        {
            const containerProto = containerCtor.prototype;
            const container = new containerCtor();
            const namespace = container.namespace;

            const funcNames = Object.keys(containerProto);

            for( const funcName of funcNames )
            {
                if( this._isClientFunction(funcName) )
                {
                    const jsFuncPtr = container[funcName];
                    const functionMap = PropertyUtils.getFunctionMap(container);

                    const invoker = ClientFunctionsController._createLocalFunctionInvoker(jsFuncPtr);
                    const funcSpec = functionMap.findFunctionSpec(funcName);

                    const clientFunction = this._buildClientFunction(funcSpec, namespace, funcName, invoker);
                    this._insertIntoSearchIndex(namespace, funcName, clientFunction);
                }
            }
        }
    }

    private _buildClientFunction(funcSpec:FunctionSpec,
                                 namespace:FunctionNamespace,
                                 funcName:FunctionName,
                                 invoker:AFunctionInvoker):ClientFunction
    {
        const funcArgs = funcSpec.args.map((paramSpec:IFunctionArgSpec) => (
            new FunctionArg(
                paramSpec.title,
                this._toArgType(namespace, funcName, paramSpec),
                paramSpec.title,
                paramSpec.tip,
                paramSpec.optional,
                paramSpec.repeatable
            )
        ));

        const returnSpec = funcSpec.returns;
        const funcRet = new FunctionReturn(this._toArgType(namespace, funcName, returnSpec), returnSpec.title, returnSpec.tip);

        const id = IdUtil.newGUID();

        return new ClientFunction(
            invoker,
            id,
            funcSpec.alias,
            funcSpec.example,
            funcSpec.lazyOnly,
            namespace,
            funcName,
            funcSpec.title,
            funcSpec.tip,
            funcSpec.icon,
            funcSpec.usesFeatures,
            funcArgs,
            funcRet
        );
    }

    private static _createLocalFunctionInvoker(jsFunc:Function):AFunctionInvoker
    {
        return (id:FunctionId, args:any[]):Promise<any> => jsFunc.apply(null, args);
    }

    private _buildCloudFunctionIndexes():void
    {
        const cloudFunctions = commonControllers.flow.getCloudFunctions();

        for( const cloudFunction of cloudFunctions )
        {
            const spec = cloudFunction.spec;
            const functionId = cloudFunction.id;
            const outputComponentId = cloudFunction.outputComponentId;

            const namespace = 'cloud';
            const invoker = ClientFunctionsController._createCloudFunctionInvoker(functionId, outputComponentId, spec);

            if( this._isClientFunction(spec.alias) )
            {

                const clientFunction = this._buildClientFunction(
                    spec,
                    namespace,
                    spec.alias,
                    invoker
                );

                this._insertIntoSearchIndex(namespace, spec.alias, clientFunction);
            }
        }
    }

    private static _createCloudFunctionInvoker(functionId:number, outputComponentId:string, cloudFunction:FunctionSpec):AFunctionInvoker
    {
        return (id:FunctionId, args:any[]):Promise<ExecResult> =>
        {
            const api:SheetAPIImpl = args[0];
            const row:IValue[] = args.slice(1);

            const sheetContext = api.getSheetContext();

            const sheetId = sheetContext.getSheetId();
            const slotId = sheetContext.getSlotId();
            const slot = commonControllers.spreadsheets.sheet.findSlotById(sheetId, slotId);

            const fields = cloudFunction.args.slice(1).map(({title, type}) => new FrameField(title, type as Type));
            const parameterValues = Frames.fromFieldsAndData(fields, [row], null);

            if( sheetContext.isLiveCell() )
            {
                return commonControllers.flow.execCloudFunctionFromSlot(slot, functionId, outputComponentId, parameterValues, FlowExecType.Stream)
                    .then(() => Frames.empty());
            }
            else
            {
                return commonControllers.flow.execCloudFunctionFromSlot(slot, functionId, outputComponentId, parameterValues, FlowExecType.REST)
                    .then((response:{success:boolean, result:any, errorMessage:string}) =>
                    {
                        if( !response )
                        {
                            return Frames.empty();
                        }

                        if( response.success )
                        {
                            if( response.result )
                            {
                                try
                                {
                                    const result:IFrame = Frames.fromJSONObject(response.result);

                                    if( result.hasErrors && result.getColumn("is_flow_error") )
                                    {
                                        return result.getCell('is_flow_error', 0) as AError;
                                    }
                                    else
                                    {
                                        return result;
                                    }
                                }
                                catch( e )
                                {
                                    return JsonUtil.convertJsonObjectToFrame(response.result);
                                }
                            }

                            return Frames.empty();
                        }
                        else
                        {
                            if( response.errorMessage )
                            {
                                return AError.fromString(response.errorMessage);
                            }

                            return EvalErrors.Data;
                        }
                    });
            }
        };
    }

    private _isClientFunction(funcName:string):boolean
    {
        return !funcName.startsWith('_') && !(funcName.toLowerCase() === 'namespace');
    }

    private _insertIntoSearchIndex(namespace:FunctionNamespace, funcName:FunctionName, clientFunction):void
    {
        const ciFuncName = funcName.toUpperCase();
        const ciQualifiedName = ClientFunctionUtil.toCIQualifiedName(namespace, funcName);

        this._qualifiedNameToFunc.set(ciQualifiedName, clientFunction);
        const alias = clientFunction.alias;

        if( alias !== undefined )
        {
            const ciAlias = alias.toUpperCase();
            if( this._aliasToFunc.has(ciAlias) )
            {
                const func = this._aliasToFunc.get(ciAlias);
                console.error(`Function with alias ${alias} is already defined for the function ${func.namespace}.${func.name}`);
                console.error(`Function ${clientFunction.namespace}.${clientFunction.name} will be accessible by its qualified name`);
            }
            else
            {
                this._aliasToFunc.set(ciAlias, clientFunction);
            }
        }
        this._nameToFunc.set(ciFuncName, clientFunction);
    }

    private _toArgType(namespace:FunctionNamespace,
                       name:FunctionName,
                       argSpec:IFunctionArgSpec):FunctionArgType
    {
        if( !argSpec.type )
        {
            console.error(`Argument type not specified on @Param decorator of ${namespace}.${name}() function`);
            return null;
        }

        return new FunctionArgType(argSpec.type);
    }

    public findFunction(expr:InvokeFunctionExpr):ClientFunction | null
    {
        const literal = FunctionUtil.parseFunctionName(expr.functionFullName.value);
        const cfController = commonControllers.spreadsheets.clientFunctions;

        return cfController.findFunctionByName(literal.namespace, literal.name);
    }

    public containsLazyOnlyFunction(ohmResult:OhmParseResult)
    {
        let containsLazyOnlyFunction = false;
        const fnController = this;

        ohmResult.rootNode.accept(new class extends BaseFormulaVisitor
        {
            preInvokeFunction(invokeFuncExpr:InvokeFunctionExpr)
            {
                const clientFunction = fnController.findFunction(invokeFuncExpr);

                if( clientFunction && clientFunction.lazyOnly )
                    containsLazyOnlyFunction = true;
            }
        }());

        return containsLazyOnlyFunction;
    }
}
